import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useInterval } from "react-use";
import Close from "@mui/icons-material/Close";
import Done from "@mui/icons-material/Done";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import Replay from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Stack,
  Typography as T,
  useTheme,
} from "@mui/material";
import { TypographyProps } from "@mui/material/Typography/Typography";
import useFiles, { useCurrentKey } from "files/hooks/useFiles";
import prettyBytes from "pretty-bytes";
import { prop, uniqBy } from "ramda";
import { MultiFileDropzone } from "~/components/FileDropzone";
import { useDebounce } from "~/hooks/useDebounce";
import {
  completeMultipartUpload,
  FILE_BROWSER_API,
  firstPresignedUrl,
  MultipartPartUrl,
  MultipartUploadSession,
  UploadPart,
} from "~/lib/fileBrowserApi";
import rumClient from "~/utils/monitoring";

const MULTIPART_LIMIT = 50 * 1024 * 1024;

const splitFileIntoChunks = (file: File): Blob[] => {
  const maxChunks = 10000;
  const minChunkSize = 15 * 1024 * 1024; // 15 MB
  const chunkSize = Math.max(minChunkSize, file.size / maxChunks);
  const chunks: Blob[] = [];
  for (let start = 0; start < file.size; start += chunkSize) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
  }
  return chunks;
};

interface UploadRowProps {
  file: File;
  handleRetry: (file: File) => Promise<void>;
  progress?: FileUploadProgress;
}

const UploadRow = ({ file, progress, handleRetry }: UploadRowProps) => {
  const hasError = progress && progress.error;
  const percentTransferred = progress?.transferred ? (progress.transferred / file.size) * 100 : 0;
  const showLoading = !hasError && percentTransferred > 0 && percentTransferred < 100;
  const textProps: TypographyProps = {
    variant: "subtitle2",
    sx: { opacity: 0.6, textWrap: "nowrap" },
  };

  return (
    <Box
      key={file.name}
      display="flex"
      gap={1}
      p={1}
      borderRadius={0.75}
      bgcolor="grey.100"
      alignItems="center"
    >
      <InsertDriveFileOutlined fontSize="small" color="inherit" />
      {file.name}
      <Box flex={1} />
      <Box display="flex" minWidth={50} justifyContent="end">
        {showLoading && (
          <T textAlign="right" {...textProps}>
            {Math.max(Math.floor(percentTransferred), 1)}%
          </T>
        )}
        {!hasError && !progress?.transferred && <T {...textProps}>Waiting</T>}
        {progress?.error && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton onClick={() => handleRetry(file)} size="small">
              <Replay sx={{ fontSize: 14 }} />
            </IconButton>
            <ErrorOutline fontSize="small" color="error" />
          </Box>
        )}
        {!hasError && percentTransferred >= 100 && <Done fontSize="small" color="success" />}
      </Box>
    </Box>
  );
};

interface FileUploadProgress {
  transferred: number;
  parts?: UploadPart[];
  uploadId?: string;
  error?: boolean;
}

const ModalBody = ({
  onClose,
  submitting,
  setSubmitting,
  uploadPrefix,
}: {
  onClose: () => void;
  submitting: boolean;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  uploadPrefix?: string;
}) => {
  const abortController = useRef<AbortController>(null);
  const currentKey = useCurrentKey();
  const { mutate } = useFiles(uploadPrefix);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<Record<string, FileUploadProgress>>({});
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const debouncedUploadSpeed = useDebounce(uploadSpeed, 100);
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  // Compute overall bytes transferred from state
  const uploadedBytes = Object.values(progress).reduce((sum, progress) => {
    return sum + (progress.transferred || 0);
  }, 0);

  const overallPercent = totalSize > 0 ? (uploadedBytes / totalSize) * 100 : 0;
  const overallPercentText = `${Math.min(Math.floor(overallPercent), 100)}%`;
  const transferRate = `${prettyBytes(debouncedUploadSpeed, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}/s`;

  const prevUploadedBytesRef = useRef<number>(uploadedBytes);
  const throughputUpdateInterval = 500;

  useInterval(
    () => {
      const delta = uploadedBytes - prevUploadedBytesRef.current;
      if (delta > 0) {
        setUploadSpeed(delta * (1000 / throughputUpdateInterval));
      }
      prevUploadedBytesRef.current = uploadedBytes;
    },
    submitting ? throughputUpdateInterval : null
  );

  useEffect(() => {
    if (retrying) {
      if (overallPercent === 100) {
        // All files are successfully uploaded, hide progress
        setRetrying(false);
      }
    }
  }, [retrying, overallPercent]);

  useEffect(() => {
    abortController.current = new AbortController();
    return () => {
      abortController.current?.abort("Upload cancelled");
    };
  }, []);

  const uploadChunk = (
    url: string,
    chunk: Blob,
    file: File,
    retry = { attemptsLeft: 3, delay: 1500 }
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      xhr.setRequestHeader("Content-Type", file.type);

      let prevLoaded = 0;

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) {
          return;
        }

        const delta = e.loaded - prevLoaded;

        setProgress((progress) => {
          const prev = progress[file.name] ?? {};
          const transferred = (prev.transferred ?? 0) + delta;
          const isMultipart = chunk.size !== file.size;
          return {
            ...progress,
            [file.name]: {
              ...prev,
              // Avoid setting as 100% if multipart, since we still need the
              // complete multipart request to successfully finish after
              transferred: isMultipart ? Math.min(transferred, file.size - 1) : transferred,
            },
          };
        });

        prevLoaded = e.loaded;
      };

      const handleRetry = () =>
        setTimeout(() => {
          uploadChunk(url, chunk, file, {
            attemptsLeft: retry.attemptsLeft - 1,
            delay: retry.delay * 2,
          })
            .then(resolve)
            .catch(reject);
        }, retry.delay);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr.getResponseHeader("ETag") || "");
        } else if (retry.attemptsLeft > 0) {
          handleRetry();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        if (retry.attemptsLeft > 0) {
          handleRetry();
        } else {
          reject(new Error("Network error"));
        }
      };

      abortController.current?.signal.addEventListener("abort", () => {
        xhr.abort();
      });
      xhr.send(chunk);
    });
  };

  const uploadMultipartFile = async (file: File, key: string) => {
    const maxParallelReq = 6;
    const chunks = splitFileIntoChunks(file);
    const fileProgress = progress[file.name];
    const parts: UploadPart[] = fileProgress?.parts ?? [];
    let uploadId = fileProgress?.uploadId;

    let initialPartUrls: MultipartPartUrl[] = [];

    if (!uploadId) {
      const startResp = await fetch(`${FILE_BROWSER_API}/multipart-uploads`, {
        method: "POST",
        signal: abortController.current?.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          key,
          file_type: file.type,
          part_count: Math.min(maxParallelReq, chunks.length),
        }),
      });

      if (!startResp.ok) {
        throw new Error("Unable to initiate multipart upload");
      }

      const session: MultipartUploadSession = await startResp.json();
      uploadId = session.upload_id;
      initialPartUrls = session.parts;

      setProgress((progress) => {
        const prev = progress[file.name] ?? {};
        return { ...progress, [file.name]: { ...prev, uploadId } };
      });
    }

    for (let startPartNum = 1; startPartNum <= chunks.length; startPartNum += maxParallelReq) {
      // Jump ahead on part number if retrying and there is already progress made from previous attempt
      if (parts.length && startPartNum === 1) {
        const existingParts = fileProgress?.parts ?? [];
        const restartPartNum = Math.max(...existingParts.map(({ part_num }) => part_num), 0);
        if (restartPartNum) {
          startPartNum = restartPartNum + 1;
        }
      }

      // Get pre-signed multi-part upload urls
      const endPartNum =
        startPartNum + Math.min(maxParallelReq, chunks.length - (startPartNum - 1));
      let partUrls = startPartNum === 1 ? initialPartUrls : [];
      if (!partUrls.length) {
        const presignedResp = await fetch(
          `${FILE_BROWSER_API}/multipart-uploads/${encodeURIComponent(uploadId)}/part-urls`,
          {
            method: "POST",
            signal: abortController.current?.signal,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key, start: startPartNum, end: endPartNum }),
          }
        );

        if (!presignedResp.ok) {
          throw new Error("Unable to generate pre-signed URLs");
        }

        partUrls = await presignedResp.json();
      }

      await Promise.all(
        partUrls.map(async ({ part_num, url }) => {
          const etag = await uploadChunk(url, chunks[part_num - 1], file);
          const part = { etag, part_num };
          parts.push(part);
          setProgress((progress) => {
            const prev = progress[file.name] ?? {};
            const prevParts = prev.parts ?? [];
            return { ...progress, [file.name]: { ...prev, parts: [...prevParts, part] } };
          });
        })
      );
    }

    const sortedParts = parts.sort((a, b) => a.part_num - b.part_num);
    const uniqueParts = uniqBy(prop("part_num"), sortedParts);

    await completeMultipartUpload(key, uploadId, uniqueParts, abortController.current?.signal);

    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return {
        ...progress,
        [file.name]: { ...prev, transferred: file.size },
      };
    });
  };

  const uploadSingleFile = async (file: File, key: string) => {
    const presignedResp = await fetch(`${FILE_BROWSER_API}/upload-urls`, {
      method: "POST",
      signal: abortController.current?.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files: [{ key, file_type: file.type }] }),
    });
    if (!presignedResp.ok) {
      throw new Error("Unable to get upload URL");
    }
    const url = firstPresignedUrl(await presignedResp.json());
    await uploadChunk(url, file, file);
    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return {
        ...progress,
        [file.name]: { ...prev, transferred: file.size },
      };
    });
  };

  const uploadFiles = async (files: File[]) => {
    for (const file of files) {
      const key = `${uploadPrefix ?? currentKey}${file.name}`;
      const fn = file.size < MULTIPART_LIMIT ? uploadSingleFile : uploadMultipartFile;
      try {
        await fn(file, key);
      } catch (err) {
        console.error(err);
        rumClient?.recordError(err);
        setError(true);
        setProgress((progress) => {
          const prev = progress[file.name];
          return {
            ...progress,
            [file.name]: { ...prev, error: true },
          };
        });
      }
    }

    setSubmitting(false);
    setComplete(true);
    await mutate();
  };

  const retryUpload = async (file: File) => {
    setError(false);
    setRetrying(true);
    setSubmitting(true);
    setProgress((progress) => {
      const prev = progress[file.name] ?? {};
      return { ...progress, [file.name]: { ...prev, error: false } };
    });
    await uploadFiles([file]);

    if (!Object.values(progress).some(({ error }) => error)) {
      setRetrying(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitting(true);
        uploadFiles(files);
      }}
    >
      <DialogTitle>
        <Box display="flex" gap={1} alignItems="center">
          {!submitting && !complete && "Upload Files"}
          {submitting && !retrying && "Uploading Files"}
          {complete && "Upload Complete"}
          <T variant="body1">
            {files.length > 0 && `(${files.length} total, ${prettyBytes(totalSize)})`}
          </T>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={submitting && !retrying}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {(submitting || retrying) && (
          <Box mb={2}>
            <T variant="label" sx={{ opacity: 1 }}>
              Total Progress
            </T>
            <LinearProgress variant="determinate" value={Math.min(overallPercent, 100)} />
            <Box display="flex" justifyContent="space-between" mt={0.5}>
              <T variant="body2">Transfer Rate: {transferRate}</T>
              <T variant="body2">{overallPercentText}</T>
            </Box>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error uploading files
          </Alert>
        )}
        {!submitting && !complete && <MultiFileDropzone files={files} setFiles={setFiles} />}
        <Fade in={submitting || complete} timeout={500} mountOnEnter>
          <Stack gap={1}>
            {files.map((file) => (
              <UploadRow
                key={file.name}
                file={file}
                progress={progress[file.name]}
                handleRetry={retryUpload}
              />
            ))}
          </Stack>
        </Fade>
      </DialogContent>
      <DialogActions>
        {!complete && !submitting && (
          <>
            <Button onClick={onClose} size="medium" color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button type="submit" disabled={!files.length} variant="contained" size="medium">
              Upload
            </Button>
          </>
        )}
        {submitting && (
          <Button onClick={onClose} size="medium" color="inherit" variant="outlined">
            Cancel Upload
          </Button>
        )}
        {complete && (
          <Button onClick={onClose} size="medium" variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </form>
  );
};

const UploadFileDialog = (props: { open: boolean; onClose: () => void; uploadPrefix?: string }) => {
  const { transitions } = useTheme();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    props.onClose();
    setTimeout(() => setSubmitting(false), transitions.duration.leavingScreen);
  };

  return (
    <Dialog
      fullWidth
      open={props.open}
      onClose={() => {
        if (!submitting) {
          handleClose();
        }
      }}
    >
      <ModalBody
        onClose={handleClose}
        submitting={submitting}
        setSubmitting={setSubmitting}
        uploadPrefix={props.uploadPrefix}
      />
    </Dialog>
  );
};

export default UploadFileDialog;
